/**
 * ElementPreparationEngine
 * 
 * Pure data preparation engine for converting raw planet data into unified elements.
 * Separates data transformation from rendering and calculation logic.
 * 
 * Architecture: Universal Layer-Based Architecture v2.0.0
 * Phase: Data Preparation Layer Implementation
 * 
 * Features:
 * - Pure data transformation functions
 * - Planet to unified element conversion
 * - Yoga grouping logic extraction
 * - Comprehensive debug traces
 * - Event-driven data preparation
 */

import { GrahaPosition, Coordinate, GrahaName } from '../../../../../models/types';
import { UnifiedElement } from './ElementCalculationEngine';
import { coordinateToLongitude } from '../../../../../utils/astrology/coordinates';
import {
    getContainerIndexForRasi,
    getHouseNumberFromRasi,
    stringToRasiName
} from '../utils/HouseMappingUtils';
import { EngineStatsTracker, DebugTraceManager } from './shared/EngineStatsTracker';

// Debug trace configuration
const DEBUG_TRACES = DebugTraceManager.create({
    PREPARATION_ENGINE: false,
    DATA_CONVERSION: false,
    YOGA_GROUPING: false,
    ELEMENT_CREATION: false,
    VALIDATION: false
});

/**
 * Configuration for element preparation
 */
export interface PreparationConfig {
    enableYogaGrouping: boolean;
    yogaGroupingMode: number; // 0 = global degrees, 1 = by rashi
    degreeTolerance: number;
    includeRetrogradePlanets: boolean;
    debugMode: boolean;
}

/**
 * Preparation result with statistics
 */
export interface PreparationResult {
    elements: UnifiedElement[];
    statistics: {
        totalPlanets: number;
        totalYogas: number;
        preparationTime: number;
        validationErrors: string[];
    };
    debugTrace?: string[];
}

/**
 * Raw planet data input
 */
export interface PlanetDataInput {
    planets: GrahaPosition[];
    lagna?: GrahaPosition;
    hora?: {
        graha: GrahaName | null;
        lord: string;
        currentPlanet?: string;
    } | null;
    metadata?: {
        chartType: string;
        calculationMethod: string;
        timestamp: number;
    };
}

/**
 * Yoga grouping result
 */
export interface YogaGroup {
    planets: GrahaPosition[];
    averageDegree: number;
    rasiIndex: number;
    containerIndex: number;
    yogaType: 'rashi-yoga' | 'global-yoga';
}

/**
 * Pure data preparation engine
 */
export class ElementPreparationEngine {
    private static instance: ElementPreparationEngine | null = null;
    private currentHoraData: { graha: GrahaName | null; lord: string } | null = null;
    private currentPlanetsData: any[] | null = null;
    private currentHoraCurrentPlanet: string | null = null;
    
    /**
     * Determine if a planet is the hora lord
     * @param planetName - The planet name (e.g., "Sy", "Ch", "Ma", etc.)
     * @returns 'hora' if the planet is the hora lord, 'none' otherwise
     */
    /**
     * Determine special type for planet using direct API field comparison
     * Simple and direct approach
     */
    private determineSpecialType(planet: any, horaCurrentPlanet?: string): 'none' | 'lagna' | 'hora' {
        if (this.isLagna(planet.short_name)) {
            return 'lagna';
        }

        const planetName = planet.graha.toString();
        if (this.isHora(planetName, horaCurrentPlanet)) {
            return 'hora';
        }

        return 'none';
    }

    /**
     * Simple comparison: is this planet Lagna?
     */
    private isLagna(shortName: string): boolean {
        return shortName === 'Lg';
    }

    /**
     * Simple comparison: is this planet the Hora lord?
     */
    private isHora(planetName: string, horaCurrentPlanet?: string): boolean {
        if (!horaCurrentPlanet) return false;
        return planetName === horaCurrentPlanet;
    }

    /**
     * Simple comparison: is this planet Chandra? (for future use)
     */
    private isChandra(planetName: string): boolean {
        return planetName === 'Chandra';
    }
    
    // Default configuration
    private defaultConfig: PreparationConfig = {
        enableYogaGrouping: true,
        yogaGroupingMode: 1, // By rashi
        degreeTolerance: 10.0,
        includeRetrogradePlanets: true,
        debugMode: true
    };

    // Performance tracking
    private statsTracker = new EngineStatsTracker(
        { totalPreparations: 0, averageTime: 0, lastPreparationTime: 0, elementsCounted: 0 },
        'totalPreparations',
        'averageTime'
    );

    private constructor() {
    }

    public static getInstance(): ElementPreparationEngine {
        if (!ElementPreparationEngine.instance) {
            ElementPreparationEngine.instance = new ElementPreparationEngine();
        }
        return ElementPreparationEngine.instance;
    }

    /**
     * Main preparation method: convert raw planet data to unified elements
     */
    public prepareElements(
        input: PlanetDataInput,
        config: Partial<PreparationConfig> = {}
    ): PreparationResult {
        const startTime = performance.now();
        const finalConfig = { ...this.defaultConfig, ...config };
        
        // Store data for special type determination
        this.currentHoraData = input.hora || null;
        this.currentPlanetsData = input.planets;

        // Extract hora current_planet from ReadyHoraData
        this.currentHoraCurrentPlanet = input.hora?.currentPlanet || null;

        const validationErrors: string[] = [];
        const elements: UnifiedElement[] = [];

        // Validate input data
        const validation = this.validateInput(input);
        if (!validation.isValid) {
            validationErrors.push(...validation.errors);
        }

        // Convert individual planets to unified elements
        const planetElements = this.convertPlanetsToElements(input.planets, finalConfig);
        elements.push(...planetElements);

        // Add lagna if provided
        if (input.lagna) {
            const lagnaElement = this.convertLagnaToElement(input.lagna, finalConfig);
            if (lagnaElement) {
                elements.push(lagnaElement);
            }
        }

        // todo: interactive option
        // // Group planets into yogas if enabled
        // if (finalConfig.enableYogaGrouping) {
        //     const yogaElements = this.createYogaElements(input.planets, finalConfig);
        //     elements.push(...yogaElements);
        // }


        // Generate debug traces
        const debugTrace = finalConfig.debugMode ? 
            this.generatePreparationTrace(input, elements, finalConfig) : undefined;

        const preparationTime = performance.now() - startTime;
        this.updatePreparationStats(preparationTime, elements.length);

        return {
            elements,
            statistics: {
                totalPlanets: planetElements.length + (input.lagna ? 1 : 0),
                totalYogas: elements.filter(e => e.type === 'yoga').length,
                preparationTime,
                validationErrors
            },
            debugTrace
        };
    }

    /**
     * Validate input planet data
     */
    private validateInput(input: PlanetDataInput): { isValid: boolean; errors: string[] } {
        const errors: string[] = [];

        if (!input.planets || input.planets.length === 0) {
            errors.push('No planet data provided');
        }

        input.planets.forEach((planet, index) => {
            if (!planet.graha) {
                errors.push(`Planet at index ${index} has no graha name`);
            }
            
            if (!planet.position) {
                errors.push(`Planet ${planet.graha} at index ${index} has undefined position`);
                return;
            }

            if (typeof planet.position.degrees === 'undefined') {
                errors.push(`Planet ${planet.graha} at index ${index} has undefined degrees in position`);
                return;
            }
            
            const longitude = coordinateToLongitude(planet.position);
            if (typeof longitude !== 'number' || longitude < 0 || longitude >= 360) {
                errors.push(`Planet ${planet.graha} has invalid longitude: ${longitude}`);
            }
            if (!planet.rasi || typeof planet.rasi !== 'string') {
                errors.push(`Planet ${planet.graha} has invalid rasi: ${planet.rasi}`);
            }
        });

        return {
            isValid: errors.length === 0,
            errors
        };
    }

    /**
     * Convert individual planets to unified elements
     */
    private convertPlanetsToElements(
        planets: GrahaPosition[],
        config: PreparationConfig
    ): UnifiedElement[] {
        const elements: UnifiedElement[] = [];

        for (const planet of planets) {
            const element = this.createPlanetElement(planet, config);
            if (element) {
                elements.push(element);
            }
        }

        return elements;
    }

    /**
     * Create unified element from single planet
     */
    private createPlanetElement(planet: GrahaPosition, config: PreparationConfig): UnifiedElement | null {
        try {
            // Convert rasi string to RasiName enum
            const rasiName = stringToRasiName(planet.rasi);
            
            // Calculate house number (1-12) from rasi using exact old logic
            const houseNumber = getHouseNumberFromRasi(rasiName);
            
            // Calculate degree within house (0-30)
            const longitude = coordinateToLongitude(planet.position);
            const degreeInHouse = longitude % 30;
            
            // Calculate container index using exact old logic
            const containerIndex = getContainerIndexForRasi(rasiName);

            // Convert graha enum to string
            const planetName = planet.graha.toString();

            const element: UnifiedElement = {
                id: `planet-${planetName}-${Date.now()}`,
                type: 'planet',
                name: planetName,
                displayName: planetName,
                position: planet,
                containerIndex,
                houseNumber,
                degree: degreeInHouse,
                special: this.determineSpecialType(planet, this.currentHoraCurrentPlanet || undefined),
                debugInfo: {
                    source: 'ElementPreparationEngine',
                    calculationTime: performance.now(),
                    originalData: { ...planet }
                }
            };

            return element;
        } catch (error) {
            return null;
        }
    }

    /**
     * Convert lagna to unified element
     */
    private convertLagnaToElement(lagna: GrahaPosition, config: PreparationConfig): UnifiedElement | null {
        // Create lagna as a special planet element
        const lagnaElement = this.createPlanetElement(lagna, config);
        if (lagnaElement) {
            lagnaElement.name = 'Lg';
            lagnaElement.displayName = 'Lg';
            lagnaElement.id = `planet-Lg-${Date.now()}`;
            lagnaElement.special = 'lagna'
        }
        return lagnaElement;
    }

    /**
     * Create yoga elements from planet groupings
     */
    private createYogaElements(planets: GrahaPosition[], config: PreparationConfig): UnifiedElement[] {
        const yogaGroups = this.groupPlanetsIntoYogas(planets, config);
        const yogaElements: UnifiedElement[] = [];

        for (const yoga of yogaGroups) {
            const element = this.createYogaElement(yoga, config);
            if (element) {
                yogaElements.push(element);
            }
        }

        return yogaElements;
    }

    /**
     * Group planets into yogas based on configuration
     */
    private groupPlanetsIntoYogas(planets: GrahaPosition[], config: PreparationConfig): YogaGroup[] {
        const yogaGroups: YogaGroup[] = [];

        if (config.yogaGroupingMode === 0) {
            // Global degrees grouping
            yogaGroups.push(...this.groupByGlobalDegrees(planets, config.degreeTolerance));
        } else {
            // By rashi grouping (default)
            yogaGroups.push(...this.groupByRashi(planets));
        }

        return yogaGroups;
    }

    /**
     * Group planets by global degree proximity
     */
    private groupByGlobalDegrees(planets: GrahaPosition[], tolerance: number): YogaGroup[] {
        const sortedPlanets = [...planets].sort((a, b) => 
            coordinateToLongitude(a.position) - coordinateToLongitude(b.position)
        );
        const groups: YogaGroup[] = [];
        let currentGroup: GrahaPosition[] = [];

        for (const planet of sortedPlanets) {
            if (currentGroup.length === 0) {
                currentGroup = [planet];
            } else {
                const lastPlanet = currentGroup[currentGroup.length - 1];
                const distance = Math.abs(
                    coordinateToLongitude(planet.position) - coordinateToLongitude(lastPlanet.position)
                );
                
                if (distance <= tolerance) {
                    currentGroup.push(planet);
                } else {
                    // Finalize current group and start new one
                    if (currentGroup.length > 1) {
                        groups.push(this.createYogaGroup(currentGroup, 'global-yoga'));
                    }
                    currentGroup = [planet];
                }
            }
        }

        // Add final group
        if (currentGroup.length > 1) {
            groups.push(this.createYogaGroup(currentGroup, 'global-yoga'));
        }

        return groups;
    }

    /**
     * Group planets by rashi (house)
     */
    private groupByRashi(planets: GrahaPosition[]): YogaGroup[] {
        const rasiGroups = new Map<string, GrahaPosition[]>();

        // Group by rasi
        for (const planet of planets) {
            if (!rasiGroups.has(planet.rasi)) {
                rasiGroups.set(planet.rasi, []);
            }
            rasiGroups.get(planet.rasi)!.push(planet);
        }

        // Convert to yoga groups (only if more than 1 planet in rasi)
        const yogaGroups: YogaGroup[] = [];
        for (const [, groupPlanets] of rasiGroups.entries()) {
            if (groupPlanets.length > 1) {
                yogaGroups.push(this.createYogaGroup(groupPlanets, 'rashi-yoga'));
            }
        }

        return yogaGroups;
    }

    /**
     * Create yoga group from planet list
     */
    private createYogaGroup(planets: GrahaPosition[], yogaType: 'rashi-yoga' | 'global-yoga'): YogaGroup {
        // Calculate average longitude
        const averageLongitude = planets.reduce((sum, p) => sum + coordinateToLongitude(p.position), 0) / planets.length;
        
        // Calculate degree within house
        const averageDegree = averageLongitude % 30;
        
        // Use first planet's rasi for rashi-yoga, calculate from longitude for global-yoga
        let houseNumber: number;
        let containerIndex: number;
        
        if (yogaType === 'rashi-yoga') {
            // Use exact old logic for rashi yoga
            const rasiName = stringToRasiName(planets[0].rasi);
            houseNumber = getHouseNumberFromRasi(rasiName);
            containerIndex = getContainerIndexForRasi(rasiName);
        } else {
            // Global yoga - calculate from longitude
            houseNumber = Math.floor(averageLongitude / 30) + 1;
            if (houseNumber > 12) houseNumber = 12;
            if (houseNumber < 1) houseNumber = 1;
            // Use same logic as planets for container index
            containerIndex = houseNumber === 12 ? 0 : houseNumber;
        }
            
        const rasiIndex = houseNumber - 1; // 0-11 for rasi index

        return {
            planets,
            averageDegree,
            rasiIndex,
            containerIndex,
            yogaType
        };
    }

    /**
     * Create unified element from yoga group
     */
    private createYogaElement(yoga: YogaGroup, config: PreparationConfig): UnifiedElement | null {
        try {
            const planetNames = yoga.planets.map(p => p.graha.toString()).join(', ');
            
            const element: UnifiedElement = {
                id: `yoga-${planetNames.replace(/\s+/g, '')}-${Date.now()}`,
                type: 'yoga',
                name: `Yoga-${yoga.planets.length}`,
                displayName: planetNames,
                position: yoga.planets[0], // Use first planet as representative
                containerIndex: yoga.containerIndex,
                houseNumber: yoga.rasiIndex + 1,
                degree: yoga.averageDegree,
                special: 'none', // Yoga elements don't have special hora status
                planets: yoga.planets,
                planetCount: yoga.planets.length,
                debugInfo: {
                    source: 'ElementPreparationEngine-Yoga',
                    calculationTime: performance.now(),
                    originalData: { yoga, yogaType: yoga.yogaType }
                }
            };

            return element;
        } catch (error) {
            return null;
        }
    }


    /**
     * Generate preparation debug trace
     */
    private generatePreparationTrace(
        input: PlanetDataInput,
        elements: UnifiedElement[],
        config: PreparationConfig
    ): string[] {
        const planetCount = elements.filter(e => e.type === 'planet').length;
        const yogaCount = elements.filter(e => e.type === 'yoga').length;

        return [
            `Input planets: ${input.planets.length}`,
            `Lagna included: ${input.lagna ? 'yes' : 'no'}`,
            `Yoga grouping: ${config.enableYogaGrouping ? 'enabled' : 'disabled'}`,
            `Yoga mode: ${config.yogaGroupingMode === 0 ? 'global degrees' : 'by rashi'}`,
            `Degree tolerance: ${config.degreeTolerance}°`,
            `Output elements: ${elements.length} (${planetCount} planets, ${yogaCount} yogas)`,
            `Validation errors: ${input.planets.length > 0 ? 'none' : 'missing data'}`
        ];
    }

    /**
     * Update preparation performance statistics
     */
    private updatePreparationStats(duration: number, elementCount: number): void {
        this.statsTracker.update(duration, (stats) => {
            stats.lastPreparationTime = duration;
            stats.elementsCounted += elementCount;
        });
    }

    /**
     * Get preparation performance statistics
     */
    public getPreparationStats() {
        return this.statsTracker.get();
    }

    /**
     * Reset preparation statistics
     */
    public resetStats(): void {
        this.statsTracker.reset();
    }

    /**
     * Enable/disable debug traces
     */
    public static setDebugTraces(enabled: boolean): void {
        DebugTraceManager.setAll(DEBUG_TRACES, enabled);
    }
}

// Export for browser console debugging
if (typeof window !== 'undefined') {
    (window as any).ElementPreparationEngine = ElementPreparationEngine;
}